import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, ConflictError } from '../../api/errors';
import { notificationsApi } from '../../api/notifications-api';
import { useAuth } from '../../auth/AuthContext';
import { SearchableSelect } from '../../components/SearchableSelect';
import type { Customer } from '../../domain/inventory';
import {
  buildSettingsPatch,
  deliveries,
  deliveryLabels,
  settingFields,
  settingLabels,
  settingsDraft,
  type NotificationSettings,
  type NotificationSettingsList,
  type SettingsDraft,
} from '../../domain/notification';

function SettingsForm({
  title,
  current,
  defaults,
  customerId,
  onDone,
}: {
  title: string;
  current: NotificationSettings;
  /** For a customer: the defaults its empty fields fall back to. */
  defaults?: NotificationSettings;
  customerId?: string;
  onDone?: () => void;
}) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SettingsDraft>(() => settingsDraft(current));
  const [errors, setErrors] = useState<Partial<Record<keyof SettingsDraft, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: notificationsApi.updateSettings.bind(notificationsApi),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'settings'] });
      onDone?.();
    },
    onError: (e) =>
      setSaveError(
        e instanceof ConflictError
          ? 'Someone else changed these settings. Reload the page to see their change.'
          : e instanceof ApiError
            ? e.message
            : 'The settings could not be saved.',
      ),
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSaved(false);
    setSaveError(null);
    const { patch, errors: found } = buildSettingsPatch(draft, current, customerId);
    setErrors(found);
    if (patch) save.mutate(patch);
  }

  const placeholder = (v: number | number[] | undefined) =>
    customerId && defaults ? `Default: ${Array.isArray(v) ? v.join(', ') : (v ?? '')}` : undefined;

  return (
    <form className="notification-settings" aria-label={title} onSubmit={submit} noValidate>
      <h3>{title}</h3>
      {saveError ? (
        <p role="alert" className="form-error">
          {saveError}
        </p>
      ) : null}
      {saved && !onDone ? <p role="status">Saved.</p> : null}
      <div className="notification-settings-grid">
        {settingFields.map((field) => (
          <label key={field}>
            {settingLabels[field].label} (days)
            <input
              inputMode="numeric"
              value={draft[field]}
              placeholder={placeholder(defaults?.[field])}
              onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
              aria-invalid={Boolean(errors[field])}
            />
            <small className="table-subtext">{errors[field] ?? settingLabels[field].hint}</small>
          </label>
        ))}
        <label>
          {settingLabels.reminderAfterDays.label} (days)
          <input
            value={draft.reminderAfterDays}
            placeholder={placeholder(defaults?.reminderAfterDays)}
            onChange={(e) => setDraft((d) => ({ ...d, reminderAfterDays: e.target.value }))}
            aria-invalid={Boolean(errors.reminderAfterDays)}
          />
          <small className="table-subtext">
            {errors.reminderAfterDays ?? settingLabels.reminderAfterDays.hint}
          </small>
        </label>
        <label>
          Who is told
          <select
            aria-label="Who is told"
            value={draft.delivery}
            onChange={(e) =>
              setDraft((d) => ({ ...d, delivery: e.target.value as SettingsDraft['delivery'] }))
            }
          >
            {customerId ? <option value="">Default</option> : null}
            {deliveries.map((d) => (
              <option key={d} value={d}>
                {deliveryLabels[d]}
              </option>
            ))}
          </select>
          <small className="table-subtext">
            Only our own owners are emailed for now; the customer’s contacts are never sent to.
          </small>
        </label>
        <label>
          Watch-only until
          <input
            type="date"
            value={draft.watchOnlyUntil}
            placeholder={
              customerId && defaults?.watchOnlyUntil
                ? `Default: ${defaults.watchOnlyUntil.slice(0, 10)}`
                : undefined
            }
            onChange={(e) => setDraft((d) => ({ ...d, watchOnlyUntil: e.target.value }))}
          />
          <small className="table-subtext">
            {customerId
              ? 'Emails start on this date. Empty: the default date.'
              : 'Emails start on this date. Empty: watch-only, nothing is sent.'}
          </small>
        </label>
      </div>
      <div className="dialog-actions">
        {onDone ? (
          <button type="button" onClick={onDone}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="primary" disabled={!canMutate || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
      {current.lastModifiedBy ? (
        <p className="drawer-version-note">
          Last changed by {current.lastModifiedBy}
          {current.lastModified ? ` on ${current.lastModified.slice(0, 10)}` : ''}.
        </p>
      ) : null}
    </form>
  );
}

/** The summary's thresholds: the defaults, and per-customer overrides. */
export function SummarySettingsTab({
  settings,
  customersById,
}: {
  settings: NotificationSettingsList;
  customersById: ReadonlyMap<string, Customer>;
}) {
  const { canMutate } = useAuth();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const name = (id: string) =>
    customersById.get(id)?.displayName ?? customersById.get(id)?.name ?? id;
  const overridden = new Set(settings.overrides.map((o) => o.customerId ?? ''));
  const customerOptions = useMemo(
    () =>
      [...customersById.values()]
        .filter((c) => !overridden.has(c.id))
        .map((c) => ({ value: c.id, label: c.displayName ?? c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customersById, settings.overrides],
  );

  return (
    <section className="panel notification-tab" aria-label="Summary settings">
      <div className="panel-heading">
        <div>
          <h2>Settings</h2>
          <p className="notification-subtitle">
            Every number of days the summary uses. A customer’s own settings replace only the fields
            set.
          </p>
        </div>
      </div>
      <SettingsForm
        key={`defaults-${settings.defaults.version}`}
        title="Defaults"
        current={settings.defaults}
      />

      <h3 className="notification-section-title">Per customer</h3>
      {settings.overrides.length === 0 && !adding ? (
        <p className="empty-note">No customer has its own settings.</p>
      ) : null}
      {settings.overrides.map((o) => {
        const id = o.customerId ?? '';
        return editing === id ? (
          <SettingsForm
            key={`${id}-${o.version}`}
            title={name(id)}
            current={o}
            defaults={settings.defaults}
            customerId={id}
            onDone={() => setEditing(null)}
          />
        ) : (
          <div key={id} className="notification-override">
            <strong>{name(id)}</strong>
            <span className="notification-muted">
              {[
                ...settingFields
                  .filter((f) => o[f] !== undefined)
                  .map((f) => `${settingLabels[f].label} ${o[f]}`),
                o.reminderAfterDays.length > 0
                  ? `${settingLabels.reminderAfterDays.label} ${o.reminderAfterDays.join(', ')}`
                  : '',
                o.delivery ? `Told: ${deliveryLabels[o.delivery]}` : '',
              ]
                .filter(Boolean)
                .join(' · ') || 'Nothing overridden'}
            </span>
            <button
              type="button"
              className="link-button"
              onClick={() => setEditing(id)}
              disabled={!canMutate}
            >
              Edit<span className="visually-hidden"> {name(id)}’s settings</span>
            </button>
          </div>
        );
      })}
      {adding ? (
        <SettingsForm
          key={`new-${adding}`}
          title={name(adding)}
          current={{ customerId: adding, reminderAfterDays: [], version: 0 }}
          defaults={settings.defaults}
          customerId={adding}
          onDone={() => setAdding('')}
        />
      ) : (
        <label className="notification-add-override">
          <span>Give a customer its own settings</span>
          <SearchableSelect
            ariaLabel="Customer for its own settings"
            value=""
            onChange={setAdding}
            options={customerOptions}
            placeholder="Choose a customer"
            disabled={!canMutate}
          />
        </label>
      )}
    </section>
  );
}
